import { useCallback, useContext, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { QueryClient } from 'react-query';
import dynamic from 'next/dynamic';
import type { NextPage } from 'next';
import Head from 'next/head';
import cn from 'classnames';
import withStore from 'context/Store/withStore';
import { LanguageContext } from 'context/Language';
import { StoreContext } from 'context/Store';
import { articles, faq } from 'api';
import {
  defaultOptions,
  getQueries,
  getSeoTitle,
  getSeoDescription,
  cacheTime,
  defaultFilters,
} from 'utils';
import { ArticleTags, Properties } from 'types';

const Container = dynamic(() => import('components/Grid/Container'));
const Row = dynamic(() => import('components/Grid/Row'));
const Col = dynamic(() => import('components/Grid/Col'));
const Wrapper = dynamic(() => import('components/Wrapper'));
const Section = dynamic(() => import('components/Layout/Section'));
const Breadcrumbs = dynamic(() => import('components/Breadcrumbs'));
const Responsive = dynamic(() => import('components/Responsive'));
const ErrorPage = dynamic(() => import('components/ErrorPage'));

const FullScreen: NextPage<Properties> = ({ data, articleList }) => {
  const { lang } = useContext(LanguageContext);
  const { url } = useContext(StoreContext);
  const [openImage, setOpenImage] = useState(false);

  const handleToggleOpenImage = () => {
    setOpenImage((s) => {
      if (s) {
        document.body.classList.remove('no-scroll');
      } else {
        document.body.classList.add('no-scroll');
      }
      return !s;
    });
  };

  const escFunction = useCallback((event) => {
    if (event.keyCode === 27) {
      setOpenImage(false);
      document.body.classList.remove('no-scroll');
    }
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', escFunction);

    return () => {
      document.removeEventListener('keydown', escFunction);
    };
  }, [escFunction]);

  if (!data) {
    return <ErrorPage status={404} />;
  }

  return (
    <>
      <Head>
        <title>{getSeoTitle(data?.i18n && data.i18n[lang]?.title)}</title>
        {data?.i18n && data?.i18n[lang]?.preview && (
          <meta name="description" content={getSeoDescription(data?.i18n[lang]?.preview)} />
        )}

        <meta property="og:url" content={url} />
        <meta name="og:title" content={getSeoTitle(data?.i18n && data?.i18n[lang]?.title)} />
        {data?.i18n && (data?.i18n[lang]?.preview || data?.i18n[lang]?.content) && (
          <meta
            property="og:description"
            content={getSeoDescription(data?.i18n[lang]?.preview || data?.i18n[lang]?.content)}
          />
        )}
        {data?.background && <meta property="og:image" content={data?.background} />}
      </Head>

      <>
        <Responsive>
          <Container className="mt-16">
            <Breadcrumbs title={data?.i18n && data?.i18n[lang]?.title} />
          </Container>
        </Responsive>
        <Container className={cn('pt-20')}>
          <Row g={4}>
            <Col size={12}>
              <Row g={3}>
                <Col
                  size={
                    12
                  } /* onClick={handleToggleOpenImage} // Uncomment this if need modal preview for background image */
                >
                  <Section
                    className={cn(data?.background ? 'section-image__history' : '')}
                    bgImage={data?.background ? data?.background : null}
                  >
                    {openImage &&
                      data?.background &&
                      createPortal(
                        <div
                          className="portal-image"
                          onClick={handleToggleOpenImage}
                          style={{ backgroundImage: `url(${data?.background})` }}
                        >
                          <div className="portal-image__close" onClick={handleToggleOpenImage} />
                        </div>,
                        document.body,
                      )}
                    {data?.background ? (
                      <div className="background-gradient">
                        <div className="section-box">
                          <h2 className="section-box__title">
                            {data?.i18n && data?.i18n[lang]?.title}
                          </h2>
                        </div>
                      </div>
                    ) : (
                      <h1 className="section__title">{data?.i18n && data?.i18n[lang]?.title}</h1>
                    )}
                  </Section>
                </Col>
                <Col size={12}>
                  <Section>
                    <div
                      className="section-text__content"
                      dangerouslySetInnerHTML={{ __html: data?.i18n && data?.i18n[lang]?.content }}
                    />
                  </Section>
                </Col>
              </Row>
            </Col>
          </Row>
        </Container>

        <div className="separated-border__sm mt-70 " />

        <Section>
          <Wrapper articleList={articleList} />
        </Section>
      </>
    </>
  );
};

FullScreen.getInitialProps = async ({ query }): Promise<Properties> => {
  const queryClient = new QueryClient(defaultOptions);

  await queryClient.prefetchQuery(
    ['data', { id: query?.slug }],
    (props) => articles.get(props, { 'Accept-Language': query?.lang }),
    { cacheTime },
  );

  await queryClient.prefetchQuery(
    [
      'articleList',
      {
        ...defaultFilters,
        page_size: 18,
        tags: [
          ArticleTags.NEWS,
          ArticleTags.ANNOUNCES,
          ArticleTags.WARNINGS,
          ArticleTags.PROMOTIONS,
        ],
      },
    ],
    (props) => articles.getList(props, { 'Accept-Language': query?.lang }),
    { cacheTime },
  );

  return getQueries(queryClient);
};

export default withStore()(FullScreen);
